'use client';

import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  HeaderEquipmentWidgetButton,
  HeaderEquipmentWidgetNavItem
} from 'components/pcc-components';
import { EquipmentWidgetProps } from './types';
import { returnFocusToNavItem } from '../../utils/common-utils';
import { Image } from 'components/image';
import { useMyEquipmentDrawer } from 'components/my-equipment/my-equipment-drawer-context';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { useFormVisibleEvent } from 'hooks/analytics/use-form-visible-event';
import {
  ADD_EQUIPMENT_DRAWER_VIEW,
  SELECT_EQUIPMENT_DRAWER_VIEW,
  STATUS_SUCCESS
} from 'constants/analyticsConstants';

export const EquipmentWidgetClient: FC<
  PropsWithChildren<EquipmentWidgetProps>
> = ({
  hasEquipmentToSelect,
  selectedEquipmentInfo,
  isMobile,
  showDivider,
  classname,
  subLabel,
  showIcon,
  isMobileSearch,
  isFeatureFlag,
  size
}) => {
  const t = useTranslations();
  const [isOpenFromNavItem, setIsOpenFromNavItem] = useState(false);
  const { fireFormVisibleEvent } = useFormVisibleEvent();

  const { addEquipmentDrawer, selectedEquipmentDrawer, selectEquipmentDrawer } =
    useMyEquipmentDrawer();

  const equipmentSelectDrawerToDisplay = isEmpty(selectedEquipmentInfo)
    ? selectEquipmentDrawer
    : selectedEquipmentDrawer;

  const drawerToDisplay = hasEquipmentToSelect
    ? equipmentSelectDrawerToDisplay
    : addEquipmentDrawer;

  const labelKey =
    isFeatureFlag || hasEquipmentToSelect
      ? 'SELECT_EQUIPMENT'
      : 'MEQ_ADD_EQUIP';
  const myEquipmentWidgetButtonId = 'headerEquipmentWidget';

  // handle opening of drawer from nav item
  const handleOpen = () => {
    fireFormVisibleEvent({
      formName: hasEquipmentToSelect
        ? SELECT_EQUIPMENT_DRAWER_VIEW
        : ADD_EQUIPMENT_DRAWER_VIEW,
      formStatus: STATUS_SUCCESS
    });
    drawerToDisplay?.openDrawer();
    setIsOpenFromNavItem(true);
  };

  // listen for drawer to close
  useEffect(() => {
    if (!drawerToDisplay?.isDrawerOpen && isOpenFromNavItem) {
      setIsOpenFromNavItem(false);
      returnFocusToNavItem(`#${myEquipmentWidgetButtonId}`);
    }
  }, [drawerToDisplay?.isDrawerOpen, isOpenFromNavItem]);

  return (
    <>
      {isMobile ? (
        <HeaderEquipmentWidgetNavItem
          isCurrent={isOpenFromNavItem}
          label={t(labelKey)}
          selectedEquipmentInfo={selectedEquipmentInfo}
          showDivider={showDivider}
          classname={classname}
          subLabel={subLabel}
          showIcon={showIcon}
          isMobileSearch={isMobileSearch}
          {...drawerToDisplay.initiatorProps}
          onClick={handleOpen}
          isFeatureFlag={isFeatureFlag}
        />
      ) : (
        <HeaderEquipmentWidgetButton
          label={t(labelKey)}
          selectedEquipmentInfo={selectedEquipmentInfo}
          imgComp={Image}
          isCurrent={isOpenFromNavItem}
          {...drawerToDisplay.initiatorProps}
          onClick={handleOpen}
          isFeatureFlag={isFeatureFlag}
          size={size}
          tooltipMessage={t('SELECT_EQUIPMENT_TO_VIEW_PARTS_THAT_FIT')}
        />
      )}
    </>
  );
};
